import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DealerPortalSectionEnumValue, getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import InventoryItemCategory from '../InventoryItemCategories/InventoryItemCategory';
import Tooling from '../Tooling/Tooling';
import Wall from '../Wall/Wall';
import ExteriorSignageQuoteLandingInfo from '../ExteriorSignageQuote/ExteriorSignageQuoteLandingInfo';
import MerchandisingProgramQuoteLandingInfo from '../MerchandisingProgramQuote/MerchandisingProgramQuoteLandingInfo';

const normalizeCollection = collectionOrWrapped => {
  if (Array.isArray(collectionOrWrapped)) {
    return collectionOrWrapped;
  }

  if (Array.isArray(collectionOrWrapped?.items)) {
    return collectionOrWrapped.items;
  }

  return [];
};

const getNodeId = node => node?.id || node?.Id;
const getNodeChildren = node =>
  Array.isArray(node?.children) ? node.children : Array.isArray(node?.Children) ? node.Children : [];

const findMenuInTree = (treeNodes, menuId) => {
  const stack = [...(Array.isArray(treeNodes) ? treeNodes : [])];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) {
      continue;
    }

    if (getNodeId(node) === menuId) {
      return node;
    }

    const children = getNodeChildren(node);
    if (children.length > 0) {
      stack.push(...children);
    }
  }

  return null;
};

const DealerPortalMenuProductDetail = () => {
  const { t } = useTranslation();
  const { sectionName, dealerPortalMenuId, dealerPortalMenuProductId } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);

  const parsedSectionValue = getDealerPortalSectionValue(sectionName);
  const section =
    parsedSectionValue > DealerPortalSectionEnumValue.None
      ? parsedSectionValue
      : DealerPortalSectionEnumValue.DisplaysToolingAndFurniture;

  const { data: menuTreeData, isLoading } = useDealerPortalMenuTree(section, accountReducer.isLoggedIn);

  const selectedMenu = useMemo(() => findMenuInTree(menuTreeData, dealerPortalMenuId), [menuTreeData, dealerPortalMenuId]);

  const normalizedProductType = useMemo(() => {
    const inventoryItems = normalizeCollection(selectedMenu?.inventoryItems || selectedMenu?.InventoryItems);
    const toolings = normalizeCollection(selectedMenu?.toolings || selectedMenu?.Toolings);
    const panelTypes = normalizeCollection(selectedMenu?.panelTypes || selectedMenu?.PanelTypes);
    const exteriorSignages = normalizeCollection(selectedMenu?.exteriorSignages || selectedMenu?.ExteriorSignages);
    const merchandisingPrograms = normalizeCollection(
      selectedMenu?.merchandisingPrograms || selectedMenu?.MerchandisingPrograms,
    );

    if (
      inventoryItems.some(
        relation => (relation?.productId || relation?.ProductId || relation?.inventoryItemId) === dealerPortalMenuProductId,
      )
    ) {
      return 'inventoryitem';
    }

    if (toolings.some(relation => (relation?.productId || relation?.ProductId) === dealerPortalMenuProductId)) {
      return 'tooling';
    }

    if (panelTypes.some(relation => (relation?.productId || relation?.ProductId) === dealerPortalMenuProductId)) {
      return 'paneltype';
    }

    if (exteriorSignages.some(relation => (relation?.productId || relation?.ProductId) === dealerPortalMenuProductId)) {
      return 'exteriorsignage';
    }

    if (merchandisingPrograms.some(relation => (relation?.productId || relation?.ProductId) === dealerPortalMenuProductId)) {
      return 'merchandisingprogram';
    }

    if (
      normalizeCollection(selectedMenu?.inventoryItemIds || selectedMenu?.InventoryItemIds).includes(
        dealerPortalMenuProductId,
      )
    ) {
      return 'inventoryitem';
    }

    if (normalizeCollection(selectedMenu?.toolingIds || selectedMenu?.ToolingIds).includes(dealerPortalMenuProductId)) {
      return 'tooling';
    }

    if (normalizeCollection(selectedMenu?.panelTypeIds || selectedMenu?.PanelTypeIds).includes(dealerPortalMenuProductId)) {
      return 'paneltype';
    }

    if (
      normalizeCollection(selectedMenu?.exteriorSignageIds || selectedMenu?.ExteriorSignageIds).includes(
        dealerPortalMenuProductId,
      )
    ) {
      return 'exteriorsignage';
    }

    if (
      normalizeCollection(selectedMenu?.merchandisingProgramIds || selectedMenu?.MerchandisingProgramIds).includes(
        dealerPortalMenuProductId,
      )
    ) {
      return 'merchandisingprogram';
    }

    return null;
  }, [selectedMenu, dealerPortalMenuProductId]);

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>{t('loading')}</Typography>
      </Box>
    );
  }

  if (normalizedProductType === 'inventoryitem') {
    return <InventoryItemCategory />;
  }

  if (normalizedProductType === 'tooling') {
    return <Tooling />;
  }

  if (normalizedProductType === 'paneltype') {
    return <Wall />;
  }

  if (normalizedProductType === 'exteriorsignage') {
    return <ExteriorSignageQuoteLandingInfo />;
  }

  if (normalizedProductType === 'merchandisingprogram') {
    return <MerchandisingProgramQuoteLandingInfo />;
  }

  return (
    <Box p={3}>
      <Typography variant="body1" color="textSecondary">
        {t('noItemsToDisplay')}
      </Typography>
    </Box>
  );
};

export default DealerPortalMenuProductDetail;
